import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	listRecentDocuments,
	listTopBookmarks
} from "client/Api/Workspace/LiveInformation/Documents/documentsLiveInformation";
import {
	ExternalServiceConfiguredEvents,
	subscribe as externalServiceSubscribe,
	unsubscribe as externalServiceUnsubscribe
} from "client_legacy/WelcomeScreen/externalServiceConfiguredEvents";
import { BookmarkModel } from "client/Api/Documents/Bookmarks/bookmarks";
import { Guid } from "client_legacy/ndaw/guid";
import {
	dontExecuteOnCriticalPath,
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { isNil } from "client/Misc/utils";
import {
	BookmarkEvent,
	subscribe as bookmarkSubscribe
} from "client_legacy/Documents/Bookmarks/events";
import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";

interface DocumentsLiveInformationProviderOptions {
	intervalInSeconds: number;
}

export interface InterestedBookmarkParty extends InterestedParty<Guid, undefined> {
	update: (items: readonly BookmarkModel[]) => void;
}

class BookmarksProvider {
	private readonly _registeredParties: Map<Guid, InterestedBookmarkParty>;
	private _cache: readonly BookmarkModel[] | null;
	private _requestMade: boolean;

	constructor() {
		this._registeredParties = new Map<Guid, InterestedBookmarkParty>();
		this._cache = null;
		this._requestMade = false;

		bookmarkSubscribe(
			BookmarkEvent.Deleted,
			(_, bookmarkIds: number[]) => {
				if (this._cache === null) {
					return;
				}

				const newCache: readonly BookmarkModel[] = this._cache
					.filter((x) => !bookmarkIds.includes(x.id));

				if (newCache.length === this._cache.length) {
					return;
				}

				this._cache = newCache;
				this.updateParties();
			}
		);
	}

	private getBookmarks() {
		if (this._registeredParties.size > 0) {
			this._requestMade = true;
			listTopBookmarks()
				.then((bookmarks: BookmarkModel[]) => {
					this._cache = bookmarks;
					this.updateParties();
					this._requestMade = true;
				})
				.catch(() => {
					this._cache = [];
					this.updateParties();
					this._requestMade = false;
				});
		}
	}

	public register(party: InterestedBookmarkParty) {
		this._registeredParties.set(party.partyId, party);

		if (!this._requestMade) {
			this.getBookmarks();
		}

		dontExecuteOnCriticalPath(
			() => {
				const latestVersionParty: InterestedBookmarkParty | undefined = this._registeredParties
					.get(party.partyId);

				if (isNil(latestVersionParty)) {
					return;
				}

				this.updateParty(latestVersionParty);
			}
		);
	}

	public forceReload(): void {
		this.getBookmarks();
	}

	public unregister(party: InterestedBookmarkParty) {
		this._registeredParties.delete(party.partyId);
	}

	private updateParties() {
		for (const party of this._registeredParties.values()) {
			this.updateParty(party);
		}
	}

	private updateParty(party: InterestedBookmarkParty) {
		const bookmarks: readonly BookmarkModel[] | null = this._cache;
		if (bookmarks !== null) {
			party.update(bookmarks);
		}
	}
}

export type RecentDocumentsResourceId = "recent-documents";

type RecentDocumentsInterests = {
	items?: boolean;
};

type RecentDocumentsResponseModel = {
	items: readonly RecentDocumentItemViewModel[];
};

export interface InterestedRecentDocumentsParty extends InterestedParty<Guid, RecentDocumentsResourceId> {
	update?: (items: readonly RecentDocumentItemViewModel[]) => void;
	informAboutSharePointNotBeingConfigured?: () => void;
}

class DocumentsLiveInformationProvider extends LiveInformationProvider<
	DocumentsLiveInformationProviderOptions,
	Guid,
	RecentDocumentsResourceId,
	RecentDocumentsInterests,
	InterestedRecentDocumentsParty,
	RecentDocumentsResponseModel
> {
	private _isSharePointConfigured: boolean;

	constructor(options: DocumentsLiveInformationProviderOptions) {
		super(options);

		const { sharePoint } = documentsConfiguration();
		this._isSharePointConfigured = sharePoint.isConfigured;

		if (!this._isSharePointConfigured) {
			const subscriptionToken: PubSubSubscriptionToken = externalServiceSubscribe(
				ExternalServiceConfiguredEvents.sharePoint,
				() => {
					this._isSharePointConfigured = true;
					// Remove subscription after all current subscribers are notified
					setTimeout(() => externalServiceUnsubscribe(subscriptionToken), 0);
					if (this.hasRegisteredParties) {
						this.forceReload();
					}
				}
			);
		}
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[RecentDocumentsResourceId, RecentDocumentsInterests]>
	): Promise<ReadonlyArray<[RecentDocumentsResourceId, RecentDocumentsResponseModel]>>
	{
		if (!this._isSharePointConfigured) {
			return Promise.reject();
		}

		const items: readonly RecentDocumentItemViewModel[] = await listRecentDocuments();

		return entries.map(
			([resourceId]) => {
				switch (resourceId) {
					case "recent-documents":
						return [resourceId, { items }];
					default:
						throw new UnreachableCaseError(resourceId);
				}
			}
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<RecentDocumentsInterests, RecentDocumentsResponseModel>,
		response: [RecentDocumentsInterests, RecentDocumentsResponseModel]
	): void {
		const [retrievedInterests, model] = response;
		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.items = model.items;
		}
	}

	protected override informAboutGlobalRetrievalFailure(party: InterestedRecentDocumentsParty): void {
		if (!this._isSharePointConfigured) {
			if (party.informAboutSharePointNotBeingConfigured) {
				party.informAboutSharePointNotBeingConfigured();
			}
			return;
		}

		super.informAboutGlobalRetrievalFailure(party);
	}

	protected informParty(
		party: InterestedRecentDocumentsParty,
		cacheItem: [RecentDocumentsInterests, RecentDocumentsResponseModel]
	): void {
		if (!this._isSharePointConfigured) {
			if (party.informAboutSharePointNotBeingConfigured) {
				party.informAboutSharePointNotBeingConfigured();
			}
			return;
		}

		const [retrievedInterests, response] = cacheItem;
		const interestedIn: RecentDocumentsInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			if (party.update) {
				party.update(response.items);
			}
		}
	}
}

export {
	DocumentsLiveInformationProvider,
	BookmarksProvider
};
